import { EZChatControllerNew } from "@axl/ez-chat-for-school";
import Bowser from "bowser";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type DeviceSizeType = "desktop" | "tablet" | "mobile";

type DeviceContextType = {
  browser: Bowser.Parser.ParsedResult;
  width: number;
  height: number;
  deviceSize: DeviceSizeType;
  isChatInited: boolean;
  isChatOpen: boolean;
  isTouch: boolean;
  chatController: EZChatControllerNew | null;
  chatUnreadCount: number;
  setChatController: (controller: EZChatControllerNew | null) => void;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
};

const DeviceContext = createContext<DeviceContextType>({} as DeviceContextType);

export const DeviceProvider: FC<PropsWithChildren> = ({ children }) => {
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);
  const [chatController, setChatController] =
    useState<EZChatControllerNew | null>(null);
  const [isChatInited, setChatInited] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  useEffect(() => {
    const func = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", func);

    return () => window.removeEventListener("resize", func);
  }, []);

  useEffect(() => {
    if (!chatController) {
      setChatInited(false);
      setIsChatOpen(false);
      return;
    }

    setIsChatOpen(chatController.visible);

    if (chatController.inited) {
      setChatInited(true);
      return;
    }

    const intervalId = window.setInterval(() => {
      if (chatController.inited) {
        setChatInited(true);
        window.clearInterval(intervalId);
      }
    }, 200);

    return () => window.clearInterval(intervalId);
  }, [chatController]);

  useEffect(() => {
    if (!chatController) {
      setChatUnreadCount(0);
      return;
    }

    setChatUnreadCount(
      chatController.selectedConversation?.unreadMessageCount ?? 0,
    );

    const unreadChanged = chatController.selectedConversationUnreadCountChanged;
    if (!unreadChanged) return;

    const dispose = unreadChanged.on((count: number) => {
      setChatUnreadCount(count);
    });

    return () => dispose.dispose();
  }, [chatController]);

  const openChat = useCallback(() => {
    chatController?.open();
    setIsChatOpen(true);
  }, [chatController]);

  const closeChat = useCallback(() => {
    chatController?.close();
    setIsChatOpen(false);
  }, [chatController]);

  const toggleChat = useCallback(() => {
    if (isChatOpen) {
      closeChat();
    } else {
      openChat();
    }
  }, [closeChat, isChatOpen, openChat]);

  const browser = useMemo(() => Bowser.parse(window.navigator.userAgent), []);

  const isTouch = useMemo(() => {
    return (
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-ignore
      navigator.msMaxTouchPoints > 0
    );
  }, []);
  const ctxValue = useMemo(
    () => ({
      chatController,
      chatUnreadCount,
      setChatController,
      openChat,
      closeChat,
      toggleChat,
      width,
      isChatInited,
      isChatOpen,
      isTouch,
      height,
      browser,
      deviceSize: ((): DeviceSizeType => {
        if (width <= 750) return "mobile";
        if (width <= 1024) return "tablet";

        return "desktop";
      })(),
    }),
    [
      browser,
      chatController,
      chatUnreadCount,
      closeChat,
      height,
      isChatInited,
      isChatOpen,
      isTouch,
      openChat,
      toggleChat,
      width,
    ],
  );

  return (
    <DeviceContext.Provider value={ctxValue}>{children}</DeviceContext.Provider>
  );
};

export const useDevice = () => useContext(DeviceContext);
